'use client'

import { formatCurrencyWhole } from '@repo/utils/numbers'
import { Link } from 'react-router-dom'
import { SxProps } from '@mui/material'
import { useAccountControlStore } from '@repo/store/useAccountControlStore'
import { useEffect } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import MUILinearProgress from '@mui/material/LinearProgress'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './AccountControlCard.module.scss'

export const AccountControlCard = ({ clientWorkspace, matterWorkspace, sx, testData }: AccountControlCardProps) => {
  const { currentMatter } = useMattersStore()
  const { accountControlBalances, fetchAccountControlBalances } = useAccountControlStore()
  const { clientMatterNo } = currentMatter || {}

  useEffect(() => {
    clientMatterNo &&
      matterWorkspace &&
      fetchAccountControlBalances({ clientMatterNo: clientMatterNo, matterWorkspace: matterWorkspace })

    clientMatterNo && clientWorkspace && fetchAccountControlBalances({ clientMatterNo: clientMatterNo })
  }, [clientMatterNo, matterWorkspace, clientWorkspace])

  function transformAccountControl(rawData: Record<string, any>) {
    return Object.keys(rawData).map((key) => {
      const nestedObject = rawData[key]
      const firstLabel = Object.keys(nestedObject)[0] // e.g. "balance"

      return {
        title: key,
        firstLabel,
        // The rest, as needed in your UI:
        balance: nestedObject.balance,
        limit: nestedObject.limit
      }
    })
  }

  const data = testData ? testData : accountControlBalances && transformAccountControl(accountControlBalances)

  const calculateBarValue = (item = { limit: null, balance: null }) => {
    const balance: number | null = item?.balance
    const limit: number | null = item?.limit

    if (balance === null || balance <= 0) return 0
    if (!limit) return 100

    if (balance <= limit) return (balance / limit) * 100
    return (limit / balance) * 100
  }

  const isBarBackgroundRed = (item = { limit: null, balance: null }) => {
    const balance: number | null = item?.balance
    const limit: number | null = item?.limit

    if (balance && limit && balance > limit) {
      return true
    }

    return false
  }

  return (
    <MUIPaper
      id="accountControlCard"
      data-testid="AccountControlCard"
      className={styles.accountControlCard}
      sx={{
        width: '100%',
        height: 'auto',
        padding: '0',
        background: 'white',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid #D8DCE5',
        position: 'relative',
        minHeight: '100%',
        ...sx
      }}
    >
      <MUITypography
        sx={{
          padding: '1rem'
        }}
        variant="h6"
      >
        Account control
      </MUITypography>

      {data ? (
        data.map((item: any) => (
          <div className={styles.progressBarWarpper} key={item.title}>
            <MUIStack direction="column">
              <MUIStack direction="row" alignItems="flex-start" justifyContent="space-between">
                <MUITypography
                  sx={{
                    textTransform: 'uppercase',
                    color: 'text.primary',
                    fontWeight: 700,
                    fontSize: '0.8rem'
                  }}
                >
                  {item.title === 'workInProgress' ? 'wip' : item.title}
                </MUITypography>
                <MUITypography
                  className={`${styles.text} ${item.limit && item.balance > item.limit ? styles.overText : ''}`}
                  sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                  data-testid="accountControlCardAmountTotal"
                >
                  £{formatCurrencyWhole(item.balance)} /{' '}
                  {item.limit ? `£${formatCurrencyWhole(item.limit)}` : 'No limit'}
                </MUITypography>
              </MUIStack>
              <MUILinearProgress
                aria-label={`${item.title === 'workInProgress' ? 'wip' : item.title}`}
                aria-labelledby="accountControlCard"
                title={`${item.title === 'workInProgress' ? 'wip' : item.title}`}
                className={`${styles.progressBar} ${isBarBackgroundRed(item) ? styles.redBackground : ''}`}
                variant="determinate"
                value={calculateBarValue(item)}
                sx={{ height: '0.5rem', borderRadius: '4px' }}
                data-testid="accountControlCardProgressBar"
              />
            </MUIStack>
          </div>
        ))
      ) : (
        <MUIStack key="no-data">
          <MUITypography>No account data</MUITypography>
        </MUIStack>
      )}
      <Link
        className={styles.link}
        to="/accounts/client-ledger"
        style={{
          display: 'flex',
          color: '#5073C3',
          cursor: 'pointer',
          textDecoration: 'none',
          alignItems: 'center',
          margin: '1.25rem 1rem 0',
          justifyContent: 'flex-end',
          width: 'auto',
          position: 'relative'
        }}
      >
        <MUITypography
          variant="body1"
          sx={{
            color: '#5073C3',
            cursor: 'pointer',
            textDecoration: 'underline',
            width: 'auto',
            position: 'absolute',
            top: '1rem'
          }}
        >
          View client ledger
        </MUITypography>
      </Link>
    </MUIPaper>
  )
}

AccountControlCard.propTypes = {
  clientWorkspace: PropTypes.bool,
  matterWorkspace: PropTypes.bool,
  sx: PropTypes.object,
  testData: PropTypes.object
}

export type AccountControlCardProps = {
  clientWorkspace?: boolean
  matterWorkspace?: boolean
  sx?: SxProps
  testData?: any
}
